import { Dialog } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { PerformancePreview } from 'components/valueCase/PerformancePreview';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';

const fetchRequirement = async (valueCaseId) =>
    get(`/performance/admin/value_cases/${valueCaseId}`);

/**
 * Generates copy of performance object without value case specific information
 * @param {*} param0 object spread of performance object properties
 * @returns copy of performance object
 */
const cleanPerformanceObject = ({
    payer_id: _payerId,
    value_case_id: _vcid,
    value_case_name: _vcname,
    funding_source: _fundingSource,
    product_service_incentives: prodServInc,
    diagnoses: _diagnoses,
    start_date: _startDate,
    end_date: _endDate,
    strat_rule_id: _stratrule,
    strat_rule_params: _stratparams,
    tasks,
    ...restPerfObject
}) => ({
    ...structuredClone(restPerfObject),
    incentive_amt: (prodServInc ?? [])[0]?.incentive_amt,
    tasks: tasks.map(({ value_case_id: _vcidTx, task_id: _tid, subtasks, ...restTask }) => ({
        ...structuredClone(restTask),
        distinctId: `task_${uuidv4()}`,
        subtasks: subtasks.map(
            ({ task_id: _tidSt, subtask_id: _stid, conditions, ...restSubtask }) => ({
                ...structuredClone(restSubtask),
                distinctId: `subtask_${uuidv4()}`,
                conditions: conditions.map(
                    ({ condition_id: _condId, subtask_id: _stidCond, ...restCond }) => ({
                        distinctId: `condition_${uuidv4()}`,
                        ...structuredClone(restCond),
                    }),
                ),
            }),
        ),
    })),
});

export const SelectExistingRequirementDialog = ({
    isOpen = undefined,
    onCancel,
    onSelect,
    currentValueCaseId = undefined,
}) => {
    const [selectedRequirement, setSelectedRequirement] = useState();
    const [performanceObject, setPerformanceObject] = useState();

    useEffect(() => {
        void (async () => {
            try {
                if (selectedRequirement) {
                    setPerformanceObject(await fetchRequirement(selectedRequirement));
                }
            } catch (err) {
                handleError('Error fetching requirement.', err);
            }
        })();
    }, [selectedRequirement]);

    const requirements = useGet('/performance/admin/value_cases');

    const formattedRequirement = (requirement) => {
        const selected = requirement.value_case_id === selectedRequirement;
        return (
            <div
                className={classNames(
                    'font-semi-bold',
                    'sds_performance__select-existing-dialog__text',
                    'sds_performance__select-existing-dialog__option',
                    {
                        'sds_performance__select-existing-dialog__option--selected': selected,
                    },
                )}
                onClick={() => setSelectedRequirement(requirement.value_case_id)}
                onKeyDown={() => setSelectedRequirement(requirement.value_case_id)}
                role="menuitem"
                tabIndex={0}
            >
                {requirement.value_case_name}
            </div>
        );
    };

    return (
        <Dialog
            dialogTitle="Select Existing Performance Requirement"
            isOpen={isOpen}
            secondaryButtonLabel="Cancel"
            onSecondaryButtonClick={() => onCancel()}
            primaryButtonLabel="Select Performance Requirement"
            onPrimaryButtonClick={() => onSelect(cleanPerformanceObject(performanceObject))}
            isPrimaryButtonDisabled={!performanceObject}
            width={1000}
            dialogContent={
                <div className="sds_performance__select-existing-dialog">
                    <div className="padding__bottom--medium" style={{ flexShrink: 0 }}>
                        <InlineMessage
                            intent={InlineMessage.Intent.WARNING}
                            label="The selected Performance Requirement will replace all existing information in the Value Case."
                            wrapText
                        />
                    </div>
                    <div className="sds_performance__select-existing-dialog__columns">
                        <div className="sds_performance__select-existing-dialog__columns--left">
                            {(requirements ?? [])
                                .filter(
                                    (requirement) =>
                                        requirement.value_case_id !== currentValueCaseId,
                                )
                                .map((requirement) => [formattedRequirement(requirement)]) ?? []}
                        </div>
                        <div className="sds_performance__select-existing-dialog__columns--right">
                            <PerformancePreview
                                performanceObject={performanceObject}
                                showDefaultSubtaskPreview
                            />
                        </div>
                    </div>
                </div>
            }
        />
    );
};

SelectExistingRequirementDialog.propTypes = {
    isOpen: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    currentValueCaseId: PropTypes.string,
};
